import React, { useState, useEffect, useRef } from 'react';

interface Building {
  bag_id: string;
  zipcode: string;
  huisnr: string;
  huisletter: string;
  id: string;
}

interface BuildingSelectorProps {
  setZipcode: React.Dispatch<React.SetStateAction<string>>;
  setHuisnummer: React.Dispatch<React.SetStateAction<string>>;
  placeholder?: string;
  className?: string;
}

const BuildingSelector: React.FC<BuildingSelectorProps> = ({
  setZipcode,
  setHuisnummer,
  placeholder = "Zoek een adres...",
  className,
}) => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [filteredBuildings, setFilteredBuildings] = useState<Building[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse CSV without using papaparse - specific to our known headers
  const parseCSV = (csvText: string): Building[] => {
    const lines = csvText.split('\n');
    // Skip header validation since we know the exact format
    
    return lines.slice(1)
      .filter(line => line.trim() !== '')
      .map((line, index) => {
        const values = line.split(',').map(value => value.trim());
        
        // Map values based on our known order: bag_id, zipcode, huisnr, huisletter
        return {
          bag_id: values[0] || '',
          zipcode: values[1] || '',
          huisnr: values[2] || '',
          huisletter: values[3] || '',
          id: values[0] || `building-${index}` // Use bag_id as id if available
        };
      });
  };

  useEffect(() => {
    if (selectedBuilding) {
      setZipcode(selectedBuilding.zipcode);
      setHuisnummer(selectedBuilding.huisnr);
    }
  }, [selectedBuilding, setZipcode, setHuisnummer]);

  useEffect(() => {
    const fetchBuildingData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/building_data.csv');
        
        if (!response.ok) {
          throw new Error('Failed to fetch building data');
        }
        
        const csvText = await response.text();
        const parsedBuildings = parseCSV(csvText);
        
        setBuildings(parsedBuildings);
        setFilteredBuildings(parsedBuildings);
        setIsLoading(false);
      } catch (err) {
        setError('Fout bij het laden van adresgegevens');
        console.error(err);
        setIsLoading(false);
      }
    };

    fetchBuildingData();
  }, []);

  // Filter buildings based on search text
  useEffect(() => {
    // Only filter when dropdown is open to avoid unnecessary work
    if (!isOpen) return;

    // Debounce search to avoid filtering on every keystroke
    const debounceTimeout = setTimeout(() => {
      if (searchText.trim() === '') {
        // Limit the number of results when showing all buildings
        setFilteredBuildings(buildings.slice(0, 100));
        return;
      }

      const searchLower = searchText.toLowerCase();
      // Use more efficient filtering and limit results
      const filtered = buildings
        .filter(building => {
          // Search through zipcode, huisnr, and huisletter
          const addressText = `${building.zipcode} ${building.huisnr}${building.huisletter}`.toLowerCase();
          return addressText.includes(searchLower);
        })
        .slice(0, 100); // Only show first 100 matches to improve performance

      setFilteredBuildings(filtered);
    }, 200); // 200ms debounce delay

    return () => clearTimeout(debounceTimeout);
  }, [searchText, buildings, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectBuilding = (building: Building) => {
    setSelectedBuilding(building);
    setSearchText(formatBuildingLabel(building));
    setIsOpen(false);
  };

  // Format building label using the Dutch address format
  const formatBuildingLabel = (building: Building) => {
    return `${building.zipcode} ${building.huisnr}${building.huisletter}`;
  };

  if (error) {
    return <div className="text-red-500 p-2 border border-red-300 rounded">{error}</div>;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            // Don't automatically open dropdown on every keystroke when backspacing
            if (!isOpen && e.target.value.length > 0) {
              setIsOpen(true);
            }
          }}
          onClick={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isLoading}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>

      {isOpen && !isLoading && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
          {filteredBuildings.length > 0 ? (
            filteredBuildings.map((building) => (
              <div
                key={building.id}
                onClick={() => handleSelectBuilding(building)}
                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-100"
              >
                {formatBuildingLabel(building)}
              </div>
            ))
          ) : (
            <div className="py-2 px-3 text-gray-500">Geen adressen gevonden</div>
          )}
        </div>
      )}
    </div>
  );
};

export default BuildingSelector;
